import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Badge from '../../components/Badge';
import Button from "../../components/Button";
import FilterPanel from "../../components/FilterPanel";
import PageHeader from "../../components/PageHeader";
import Select from "../../components/Select";
import Table from "../../components/Table";
import { api } from "../../services/api";

export default function AdminPenalties() {
  const [penalties, setPenalties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    resolved: "",
    studentId: "",
  });
  // toast is now used for notifications

  useEffect(() => {
    loadPenalties();
  }, [filters]);

  const loadPenalties = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.resolved) params.append("resolved", filters.resolved);
      if (filters.studentId) params.append("studentId", filters.studentId);

      const response = await api.get(`/admin/penalties?${params.toString()}`);
      setPenalties(response.penalties || []);
    } catch (error) {
      toast.error("Failed to load penalties");
    } finally {
      setLoading(false);
    }
  };

  const handleResolve = async (penaltyId) => {
    try {
      await api.put(`/admin/penalties/${penaltyId}/resolve`);
      toast.success("Penalty resolved successfully");
      loadPenalties();
    } catch (error) {
      toast.error(error.error || "Operation failed");
    }
  };

  const columns = [
    {
      header: "Student",
      sortable: false,
      render: (penalty) => (
        <Link
          to={`/admin/users/${penalty.studentId?._id}`}
          className="text-primary hover:opacity-80"
        >
          {penalty.studentId?.name?.first} {penalty.studentId?.name?.last}
        </Link>
      ),
    },
    {
      header: "Type",
      accessor: "infractionType",
      sortable: true,
      render: (penalty) => (
        <span className="opacity-70 capitalize">
          {penalty.infractionType.replace("_", " ")}
        </span>
      ),
    },
    {
      header: "Points",
      accessor: "penaltyPoints",
      sortable: true,
      render: (penalty) => (
        <span className="font-medium">{penalty.penaltyPoints}</span>
      ),
    },
    {
      header: "Date",
      accessor: "createdAt",
      sortable: true,
      render: (penalty) => (
        <span className="opacity-70">
          {dayjs(penalty.createdAt).format("MMM D, YYYY")}
        </span>
      ),
    },
    {
      header: "Status",
      accessor: "resolved",
      sortable: true,
      render: (penalty) => (
        <Badge
          label={penalty.resolved ? "Resolved" : "Active"}
          variant={penalty.resolved ? "success" : "error"}
          size="small"
          className="font-medium"
        />
      ),
    },
    {
      header: "Actions",
      sortable: false,
      render: (penalty) =>
        !penalty.resolved && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleResolve(penalty._id)}
          >
            Resolve
          </Button>
        ),
    },
  ];

  const statusOptions = [
    { value: "", label: "All" },
    { value: "false", label: "Active" },
    { value: "true", label: "Resolved" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <PageHeader title="Penalty Management" />

      <FilterPanel>
        <Select
          label="Status"
          value={filters.resolved}
          onChange={(e) => setFilters({ ...filters, resolved: e.target.value })}
          options={statusOptions}
        />
      </FilterPanel>

      <Table
        columns={columns}
        data={penalties.map((p) => ({ ...p, id: p._id }))}
        loading={loading}
        emptyMessage="No penalties found"
      />
    </div>
  );
}
